// =============================================================================
// Optim TURPE v1 — DATATION et RÉSERVE DE FRAÎCHEUR du rapport servi (DEV-1621).
//
// Intention : « tout ce qui est servi au client est justifié : la date de calcul,
// la fenêtre analysée, et une réserve quand la donnée manque. Jamais une promesse
// fausse. »
//
// Le fait (lecture seule `homeys-data-prod`, 2026-09-04T11:52Z → 12:28Z) : sur le
// bâtiment 692ffeac0f28d5945f3d9832, la route du menu sert l'`Analyse`
// `6a3ba829552c8af21bc2fe53` (`optimturpe_C4`, SUCCESS), calculée le
// 2026-06-24T09:49:29Z sur la période `15/02/2025 - 15/02/2026`. Elle était donc
// servie le 04/09 avec une fenêtre close depuis 201 jours et un calcul vieux de
// 72 jours, pendant que le compteur (`Source.monitoring.datelastdata`) n'avait
// plus transmis depuis le 2026-08-24. L'écran n'en disait rien.
//
// Mesure du parc le même jour : 87 829 des 88 117 rapports servis (dernier SUCCESS
// par compteur) portent une fenêtre close depuis plus de 30 jours ; parmi les
// 9 854 compteurs dont la dernière mesure est lisible, 7 695 cumulent cette
// fenêtre close et un silence de plus de 7 jours.
// =============================================================================

import { formatDate } from "@core/helpers"

/**
 * Silence au delà duquel on pose la réserve, en jours.
 *
 * ⚠️ Ce n'est PAS l'attendu de fraîcheur du compteur. L'attendu se juge par
 * segment et sur la mesure, jamais sur un seuil plat (retour du 26/08). Tant que
 * le verdict par segment n'est pas servi à cet écran (DEV-1592), ce plancher est
 * un MINORANT volontaire : il ne déclenche la réserve que sur des retards que
 * personne ne conteste, et il ne peut pas en fabriquer une pour un compteur à
 * jour. Le témoin de DEV-1621 est muet depuis 11 jours, donc largement au dessus.
 */
export const SILENCE_MINIMAL_POUR_RESERVE_JOURS = 7

/** Millisecondes d'une journée. */
const JOUR_MS = 24 * 60 * 60 * 1000

export type ReserveFraicheurEntree = {
    /** `Analyse.createdAt` du rapport servi (ISO UTC rendu par l'API métier). */
    calculeLe?: string | Date | null
    /**
     * Bornes de la fenêtre analysée, **déjà formatées par le moteur**
     * (`data.date_*_periode_analyse.str_value`, ou les deux moitiés de
     * `data.period`).
     *
     * ⚠️ On ne reformate PAS ces bornes ici. Le moteur v1 persiste des dates
     * naïves en heure de Paris (`"2025-02-14 23:00:00"` pour un 15/02) : les
     * relire avec `moment` sans fuseau rendrait « 14/02/2025 » à l'écran alors
     * que le rapport, son PDF et son `str_value` disent tous « 15/02/2025 ».
     */
    fenetreDu?: string | null
    fenetreAu?: string | null
    /** Dernière mesure connue du compteur (`monitoring.lastdate`, ISO UTC). */
    derniereMesure?: string | Date | null
    /** Silence minimal avant de poser la réserve (défaut : la constante ci dessus). */
    silenceMinimalJours?: number
    /** Injectable pour les tests ; défaut `new Date()`. */
    maintenant?: Date
}

export type ReserveFraicheur = {
    /**
     * Phrase de datation. Toujours servie dès que la date de calcul est connue :
     * elle ne dépend d'aucun seuil et n'affirme que ce que le document porte.
     */
    datation: string
    /**
     * Réserve de collecte. Chaîne vide tant que le compteur transmet dans
     * l'attendu, ou tant qu'on ne connaît pas sa dernière mesure : on ne sert
     * jamais une réserve que l'on n'a pas vérifiée.
     */
    reserve: string
    /** Vrai quand la réserve est posée (l'écran la sert alors en avertissement). */
    estAlerte: boolean
}

/** Date lisible, ou `null` si l'entrée est absente ou illisible. */
function versDate(valeur: string | Date | null | undefined): Date | null {
    if (valeur === null || valeur === undefined || valeur === "") return null
    const d = valeur instanceof Date ? valeur : new Date(valeur)
    return Number.isNaN(d.getTime()) ? null : d
}

/**
 * Dernière mesure d'un compteur d'après `monitoring.lastdate` : le MAXIMUM sur
 * toutes les variables et toutes les granularités.
 *
 * Prendre une variable au hasard donnerait un compteur muet dès qu'une seule de
 * ses variables l'est ; le maximum ne déclenche la réserve que si PLUS RIEN
 * n'arrive, ce qui est exactement ce que la phrase servie affirme.
 * @param lastdate - Bloc `monitoring.lastdate` de la source.
 * @returns Date ISO de la mesure la plus récente, ou `null` si rien n'est lisible.
 */
export function derniereMesureConnue(
    lastdate: Record<string, Partial<Record<string, string>>> | undefined | null
): string | null {
    if (!lastdate || typeof lastdate !== "object") return null
    let meilleure: number | null = null
    let brute: string | null = null
    for (const parVariable of Object.values(lastdate)) {
        if (!parVariable || typeof parVariable !== "object") continue
        for (const valeur of Object.values(parVariable)) {
            const d = versDate(valeur ?? null)
            if (!d) continue
            const ms = d.getTime()
            if (meilleure === null || ms > meilleure) {
                meilleure = ms
                brute = typeof valeur === "string" ? valeur : d.toISOString()
            }
        }
    }
    return brute
}

/**
 * Construit la datation et, si le compteur ne transmet plus, la réserve à servir
 * au dessus du rapport.
 *
 * @param entree - Date de calcul, fenêtre analysée, dernière mesure du compteur.
 * @returns `null` quand la date de calcul est inconnue (rien à dater, donc rien à
 *   affirmer), sinon la datation et la réserve éventuelle.
 */
export function construireReserveFraicheur(
    entree: ReserveFraicheurEntree
): ReserveFraicheur | null {
    const calculeLe = versDate(entree.calculeLe)
    if (!calculeLe) return null

    const du = (entree.fenetreDu ?? "").trim()
    const au = (entree.fenetreAu ?? "").trim()
    const jourCalcul = formatDate(calculeLe, "DD/MM/YYYY")
    const datation =
        du && au
            ? `Rapport calculé le ${jourCalcul} sur la période du ${du} au ${au}.`
            : `Rapport calculé le ${jourCalcul}.`

    const derniere = versDate(entree.derniereMesure)
    if (!derniere) return { datation, reserve: "", estAlerte: false }

    const maintenant = entree.maintenant ?? new Date()
    const silence = (maintenant.getTime() - derniere.getTime()) / JOUR_MS
    const seuil =
        entree.silenceMinimalJours ?? SILENCE_MINIMAL_POUR_RESERVE_JOURS
    if (silence <= seuil) return { datation, reserve: "", estAlerte: false }

    const jourMesure = formatDate(derniere, "DD/MM/YYYY")
    return {
        datation,
        reserve: `Ce compteur ne transmet plus de mesures depuis le ${jourMesure}, le rapport n'est pas actualisé.`,
        estAlerte: true,
    }
}
